<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="es">
        <section class="intro">
          <p>
            Compartir fotos 360° en redes sociales llega a una audiencia mucho
            mayor que publicar solo en tu web. Pero cada plataforma gestiona el
            contenido 360° de forma diferente: Facebook lo muestra de forma
            interactiva, Instagram no admite 360° nativo, YouTube solo admite
            vídeo 360°. Entender estas diferencias determina si tu contenido
            llega a miles de personas o solo a un puñado.
          </p>
        </section>

        <section>
          <h2>Soporte 360° plataforma por plataforma</h2>

          <h3>Facebook: soporte nativo completo</h3>
          <p>
            Facebook es la mejor plataforma para compartir fotos 360°. Al subir
            un JPEG equirectangular con los metadatos XMP Photo Sphere
            correctos, Facebook detecta automáticamente el formato 360° y lo
            muestra como visor esférico interactivo. Los usuarios pueden rotar,
            inclinar y hacer zoom directamente en su feed, tanto en escritorio
            como en móvil.
          </p>
          <p>
            <strong>Cómo publicar en 360° en Facebook:</strong> sube el archivo
            fuente equirectangular directamente como foto (no por enlace). No
            compartas vía URL — Facebook solo activa el visor 360° para imágenes
            subidas directamente. Los archivos exportados desde Travvir,
            Insta360, Ricoh Theta y la app de Google Street View incluyen estos
            metadatos automáticamente.
          </p>
          <p>
            <strong>Impacto en el engagement:</strong> las fotos 360° en
            Facebook reciben de media <strong>3× más interacciones</strong> que
            las fotos estándar y <strong>4× más compartidos</strong>.
          </p>

          <h3>Instagram: sin soporte 360° nativo</h3>
          <p>
            Instagram no muestra fotos 360° de forma interactiva. Si subes una
            imagen equirectangular, aparece como un rectángulo plano muy
            distorsionado.
          </p>
          <p>
            <strong>Mejor enfoque para Instagram:</strong> exporta un recorte
            plano "preview" desde tu imagen 360° — un gran angular o vista doble
            que muestre la perspectiva más interesante. En el pie de foto,
            dirige a tus seguidores al tour interactivo completo mediante un
            enlace (en la bio o en el sticker de enlace de Stories).
            Alternativamente, graba un corto vídeo de barrido por el tour 360° y
            publícalo como Reel.
          </p>

          <h3>YouTube: solo vídeo 360°</h3>
          <p>
            YouTube admite vídeo 360° con visualización interactiva completa,
            pero no fotos fijas 360°. Para compartir contenido 360° en YouTube,
            crea un vídeo 360° — ya sea grabando un barrido filmado o
            convirtiendo tus escenas en vídeo tipo presentación.
          </p>

          <h3>Google Maps / Street View</h3>
          <p>
            Google Maps admite la publicación directa de fotos 360° en Street
            View. Usa la app de Google Street View para publicar tus
            equirectangulares. Estas fotos aparecen en Street View, en tu Perfil
            de Google Business y pueden mejorar tu SEO local.
          </p>
        </section>

        <section>
          <h2>Checklist antes de compartir una foto 360°</h2>
          <ul>
            <li>Archivo JPEG equirectangular con proporción 2:1</li>
            <li>
              Metadatos XMP Photo Sphere presentes (verifica con Exif Viewer)
            </li>
            <li>Resolución mínima de 4.096 × 2.048 px para Facebook</li>
            <li>Tamaño de archivo inferior a 30 MB para Facebook</li>
            <li>Subida directa como foto, nunca por enlace</li>
          </ul>
        </section>

        <section>
          <h2>Preguntas frecuentes</h2>

          <h3>
            ¿Por qué mi foto 360° no se muestra en modo interactivo en Facebook?
          </h3>
          <p>
            Verifica que los metadatos XMP Photo Sphere estén presentes en tu
            archivo y que hayas subido la imagen directamente (no por enlace).
            Las imágenes redimensionadas o reprocesadas pierden a veces estos
            metadatos.
          </p>

          <h3>
            ¿Se puede compartir un tour completo (multi-escenas) en redes
            sociales?
          </h3>
          <p>
            No directamente. Comparte el enlace de tu tour Travvir en tu
            publicación, o usa una foto 360° individual como gancho visual para
            dirigir a los seguidores a la experiencia completa.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Seguir Leyendo</h2>
          <ul>
          <li><a href="/blog/es/que-es-foto-360">¿Qué es una Foto 360°? Guía Completa 2026</a></li>
          <li><a href="/blog/es/fotos-360-con-celular">Cómo Tomar Fotos 360° con tu Celular (Sin Cámara Especial) 2026</a></li>
          <li><a href="/blog/es/integrar-tour-virtual-sitio-web">Cómo Integrar un Tour Virtual 360° en tu Sitio Web 2026</a></li>
          <li><a href="/blog/es/tour-virtual-360-principiantes">Tour Virtual 360° para Principiantes: Crea tu 1er Tour desde el Móvil</a></li>
          <li><a href="/blog/es/analitica-tour-virtual">Analítica de Tours Virtuales: Métricas Clave y Optimización</a></li>
          <li><a href="/blog/es/tour-virtual-360-guia-completa">Tour Virtual 360°: Guía Completa para Principiantes (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "compartir-foto-360-redes-sociales",
);

useBlogSeo({
  title: "Cómo Compartir Fotos 360° en Redes Sociales 2026 | Travvir",
  description:
    "Guía completa para compartir fotos 360° en Facebook, Instagram, YouTube y Google Maps. Formatos requeridos, metadatos y trucos de engagement.",
  keywords:
    "compartir foto 360 redes sociales, foto 360 facebook, foto 360 instagram, compartir tour virtual redes sociales",
  slug: "compartir-foto-360-redes-sociales",
  image: postData?.image,
  postData,
  lang: "es",
  faqItems: [
    {
      question:
        "¿Por qué mi foto 360° no se muestra en modo interactivo en Facebook?",
      answer:
        "Verifica los metadatos XMP Photo Sphere y sube la imagen directamente, no por enlace.",
    },
    {
      question: "¿Se puede compartir un tour multi-escenas en redes sociales?",
      answer:
        "No directamente. Comparte el enlace Travvir con una foto 360° individual como gancho.",
    },
  ],
});

</script>
